/**
 * Browser-side client for the fulfillment team's ERP database.
 *
 * There is no connection string in here and there must never be one: anything
 * VITE_-prefixed is compiled into the public bundle, and a browser cannot
 * speak PostgreSQL's wire protocol anyway. `ERP_DATABASE_URL` lives in the
 * Static Web App's application settings and is read only by
 * `api/src/functions/erpOrders.ts`.
 *
 * Rows arrive keyed by the ERP's own DISPLAY column names ("SKU Code", "Due
 * Date"), taken from ORDER_DATABASE_COLUMN_MAP in their db.py, so a table here
 * can carry the same headers their own Order Database page shows.
 */

import { apiTokenIssue, getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

import type { ErpColumnValues, ErpTablePage, FetchErpTableParams } from "./erpTableUrl";
import { buildErpColumnValuesUrl, buildErpTableUrl } from "./erpTableUrl";

export type {
  ErpTableRow, ErpTablePage, FetchErpTableParams, ErpColumnValues,
} from "./erpTableUrl";
export { buildErpTableUrl, buildErpColumnValuesUrl } from "./erpTableUrl";

/**
 * The ERP table this screen reads is not in the Azure copy of the database
 * (the API answers 404 with tableMissing). Not a failure of the screen and not
 * a registry typo, so it is its own class and screens say so plainly.
 */
export class ErpTableMissingError extends Error {
  constructor(public readonly table: string) {
    super(`Table not yet on Azure (${table}).`);
    this.name = "ErpTableMissingError";
  }
}

/** Throw the right error for a non-ok response. */
async function failFrom(res: Response): Promise<never> {
  let body: { error?: string; tableMissing?: boolean; table?: string } = {};
  try { body = (await res.clone().json()) as typeof body; } catch { /* not JSON */ }
  if (res.status === 404 && body.tableMissing) {
    throw new ErpTableMissingError(body.table ?? "this table");
  }
  throw new Error(body.error || `Request failed (${res.status}).`);
}

/**
 * No access token for our own API yet.
 *
 * Thrown rather than calling anyway. Calling anyway is what produced the
 * confusing failure: with no auth header, Static Web Apps' own infrastructure
 * token reaches the Function and gets rejected for having an audience of
 * "...azurewebsites.net/azurefunctions" — a message about Azure's plumbing, for
 * a problem that is simply "this account has not consented to the API scope".
 */
export class ErpConsentRequiredError extends Error {
  constructor() {
    const why = apiTokenIssue();
    // A lapsed session is not "never granted": a SPA refresh token lasts a day
    // and the quiet renewal needs third-party cookies Edge may block. Say which.
    const lapsed = /refresh_token_expired|interaction_required|monitor_window_timeout|login_required/.test(why);
    super(
      (lapsed
        ? "Your i-SYNERGY API sign-in has expired. Press Grant access to renew it - a quick Microsoft sign-in, then this screen reloads."
        : "This account has not yet granted access to the i-SYNERGY API. " +
          "Grant it once and this screen will keep working.") +
        (why ? ` (${why})` : "")
    );
    this.name = "ErpConsentRequiredError";
  }
}

export async function fetchErpTable(params: FetchErpTableParams): Promise<ErpTablePage> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch(buildErpTableUrl(params), { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as ErpTablePage;
}

/**
 * One column's ▾ dropdown values.
 *
 * Asked for when a dropdown is first opened rather than with the page — see
 * the note in api/src/functions/erpTableValues.ts for why their own route
 * cannot do that and this can.
 */
export async function fetchErpColumnValues(
  table: string, column: string, extra?: Readonly<Record<string, string>>
): Promise<ErpColumnValues> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch(buildErpColumnValuesUrl(table, column, extra), {
    headers: apiAuthHeaders(token),
  });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as ErpColumnValues;
}

/** Which columns of a table have a ▾ header dropdown (their FILTERABLE_MAX_DISTINCT rule). */
const filterableCache = new Map<string, Promise<string[] | null>>();
export function fetchErpFilterableColumns(table: string): Promise<string[] | null> {
  let hit = filterableCache.get(table);
  if (!hit) {
    hit = (async () => {
      try {
        const token = await getApiTokenSilent();
        if (!token) return null;
        const res = await fetch(buildErpColumnValuesUrl(table, "*"), { headers: apiAuthHeaders(token) });
        if (!res.ok) return null;
        const body = (await res.json()) as { filterable?: string[] };
        return body.filterable ?? null;
      } catch {
        return null;
      }
    })();
    filterableCache.set(table, hit);
    hit.then((v) => { if (v === null) filterableCache.delete(table); });
  }
  return hit;
}

/**
 * A write. POSTs to /api/erp/write, which holds all four gates.
 *
 * The body says `dryRun: false` explicitly — the endpoint defaults to a plan,
 * so a caller that forgets the flag changes nothing.
 */
export interface IErpWriteResult {
  kind?: string;
  /** A blank-row insert's new id (Order Monitoring ADD). */
  insertedId?: number;
  written?: number;
  synced?: number;
  logged?: number;
  syncs?: string[];
  /** Keys whose rows their DB layer skips rather than refusing — Drop-off's
   * already-present Tracking No.s. Reported so a short import is not read as
   * a complete one. */
  skipped?: string[];
  skipNotes?: string[];
  /** A MARK's, COPY's or TRANSFER's own counts — their result dicts. */
  tally?: Record<string, number>;
  /** A RECONCILE's derived verdict, which their own flash quotes back. */
  verdict?: string;
  /**
   * What a MARK actually wrote, by column.
   *
   * For a caller that has to PRINT the same values it stored — Drop-off's
   * manifest, where their rule is that the paper and the table always agree.
   * Computing the instant in the browser as well would give two answers
   * seconds apart, so the write is the source and the paper is rendered from
   * this.
   */
  stamped?: Record<string, string>;
  /**
   * AUTO DELETE's reserved_locations: the RES rows whose Reserved column was
   * set, by id.
   *
   * Their own answer carries it "so the DELETING modal's caller can patch the
   * handful of still-visible rows ... instead of reloading/re-rendering the
   * whole Monitoring table" — and here the point is different but no smaller:
   * a RES row STAYS on the table, so if the screen did not show its new shelf
   * the run would look as though it had done nothing to it.
   */
  reserved?: Record<number, string>;
  audited?: boolean;
  dryRun?: boolean;
  /**
   * A dryRun read of an insert action with a sequential column — Inbound's
   * "Next MFG No." box. A preview only: the real value is always recomputed
   * fresh, inside the write's own transaction, at save time.
   */
  nextSequential?: string;
  /**
   * An AR EXTRACT only (TIKTOK's channel): true when a not-found Order No.
   * paused the whole extraction. Nothing was written — every query the
   * server ran to reach this was a read. Re-post the SAME body with
   * `confirmed: true` to proceed with whatever WAS found.
   */
  needsConfirm?: boolean;
  /** The Order Nos. behind that pause — accepted, but not found in Order
   * Completed. */
  notFoundIds?: string[];
}

export async function erpWrite(body: unknown): Promise<IErpWriteResult> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/write", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as IErpWriteResult;
}

export interface IManageAccount {
  username: string; signinEmail: string; displayName: string; role: string; active: boolean; createdAt: string;
}
export interface IManageUsersView {
  accounts: IManageAccount[];
  roleChoices: string[];
  fullAccessRoles: string[];
  accessLevels: ("none" | "viewer" | "full")[];
  selectedRole: string;
  roleCounts: Record<string, { viewer: number; full: number }>;
  permissionRows: { moduleKey: string; label: string; category: string; categoryOrder: number; level: "none" | "viewer" | "full" }[];
  canWrite: boolean;
}

/** Manage Users (their /manage_users). 403 unless the account is an ERP administrator. */
export async function fetchManageUsers(role?: string): Promise<IManageUsersView> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch(`/api/erp/manage-users${role ? `?role=${encodeURIComponent(role)}` : ""}`, { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as IManageUsersView;
}

/** addUser / updateUser / permissions — returns their flash message. */
export async function postManageUsers(body: Record<string, unknown>): Promise<string> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/manage-users", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) return failFrom(res);
  return String(((await res.json()) as { message?: string }).message ?? "Saved.");
}

export interface IErpMe {
  mapped: boolean; username?: string; role?: string; displayName?: string;
  fullAccess: boolean; systemAdmin: boolean;
  levels?: Record<string, "none" | "viewer" | "full">;
}
let meCache: Promise<IErpMe> | undefined;
/** Which ERP account the signed-in email holds (cached for the tab). */
export function fetchErpMe(force = false): Promise<IErpMe> {
  if (!meCache || force) {
    meCache = (async () => {
      const token = await getApiTokenSilent();
      if (!token) return { mapped: false, fullAccess: false, systemAdmin: false };
      const res = await fetch("/api/erp/me", { headers: apiAuthHeaders(token) });
      if (!res.ok) return { mapped: false, fullAccess: false, systemAdmin: false };
      return (await res.json()) as IErpMe;
    })().catch(() => ({ mapped: false, fullAccess: false, systemAdmin: false }));
  }
  return meCache;
}

export interface ISyncControl {
  enabled: boolean;
  changedBy: string | null;
  changedAt: string | null;
  laptopSeenAt: string | null;
  laptopSeenSecondsAgo: number | null;
  laptopNote: string | null;
}

/** The localhost → Azure copy switch (GET reads it, POST flips it). */
export async function fetchSyncControl(): Promise<ISyncControl> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/sync-control", { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as ISyncControl;
}

export async function setSyncControl(enabled: boolean): Promise<ISyncControl> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/sync-control", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as ISyncControl;
}

/** Their /sales_report/data, computed server-side by api/src/shared/salesReport.ts. */
export interface ISalesReportParams {
  from?: string;
  to?: string;
  channels?: readonly string[];
  basis?: "date" | "ordered";
  fallback?: boolean;
  period?: "day" | "week" | "month" | "all";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchSalesReport(p: ISalesReportParams): Promise<any> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const qs = new URLSearchParams();
  if (p.from) qs.set("from", p.from);
  if (p.to) qs.set("to", p.to);
  if (p.channels?.length) qs.set("channels", p.channels.join(","));
  if (p.basis) qs.set("basis", p.basis);
  if (p.fallback === false) qs.set("fallback", "0");
  if (p.period) qs.set("period", p.period);
  const res = await fetch(`/api/erp/sales-report?${qs.toString()}`, { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return res.json();
}

/** Their /ecommerce_report/data — api/src/shared/ecommerceReport.ts. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchEcommerceReport(p: { from?: string; to?: string; period?: string }): Promise<any> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const qs = new URLSearchParams();
  if (p.from) qs.set("from", p.from);
  if (p.to) qs.set("to", p.to);
  if (p.period) qs.set("period", p.period);
  const res = await fetch(`/api/erp/ecommerce-report?${qs.toString()}`, { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return res.json();
}

/** Their /inbound_report/data and /rtg_dashboard/data — api/src/shared/stockReports.ts. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchReport(path: string, params: Record<string, string | undefined>): Promise<any> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const res = await fetch(`/api/erp/${path}?${qs.toString()}`, { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return res.json();
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchInboundReport = (p: { from?: string; to?: string; period?: string; excludeSpecial?: boolean; detail?: boolean }): Promise<any> =>
  fetchReport("inbound-report", {
    from: p.from, to: p.to, period: p.period, exclude_special: p.excludeSpecial ? "1" : undefined, detail: p.detail ? "1" : undefined,
  });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchRtgDashboard = (p: { from?: string; to?: string; detail?: boolean }): Promise<any> =>
  fetchReport("rtg-dashboard", { from: p.from, to: p.to, detail: p.detail ? "1" : undefined });

/** Their System Administration > Delete All Data — api/src/functions/erpDeleteAllData.ts. */
export interface IDeleteAllCounts { row_counts: Record<string, number>; total_rows: number }

export async function fetchDeleteAllCounts(): Promise<IDeleteAllCounts> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/delete-all-data", { headers: apiAuthHeaders(token) });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as IDeleteAllCounts;
}

export async function deleteAllData(confirmText: string, adminPassword: string): Promise<{ total_rows: number; tables_cleared: number }> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/delete-all-data", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ confirm_text: confirmText, admin_password: adminPassword }),
  });
  if (!res.ok) return failFrom(res);
  return (await res.json()) as { total_rows: number; tables_cleared: number };
}

/** fetch() with the signed-in user's API token. Their Developer settings page calls its two endpoints through this
 * (window.__erpFetch) because their own page relied on a session cookie that i-SYNERGY does not have. */
export async function erpApiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = await getApiTokenSilent();
  const headers = { ...(init.headers as Record<string, string> | undefined), ...(token ? apiAuthHeaders(token) : {}) };
  return fetch(url, { ...init, headers });
}
